import React, { useState } from "react";
import { useLocation } from 'react-router';
import { Link } from "react-router-dom";
import * as CaptchaLib from "@repoflow/browser/captcha";
import { useCaptcha } from "@repoflow/components/captcha";

import AuthSchema from "@common/schema/auth";
import * as Loading from "@repoflow/components/loading";

import { Component as Form } from "@repoflow/form-schema";
import { Container, Col, Row, Alert } from "reactstrap";
import * as Section from "@repoflow/components/section";
import { gql } from "@apollo/client";

const RESET = gql`
  mutation Reset(
    $password: String!
    $validation: String!
    $token: String!
  ) {
    viewer {
      anonymous(token: $token) {
        auth {
          reset(validation: $validation, password: $password)
        }
      }
    }
  }
`;

// The link is the credential. Landing here without one is not an error state to
// recover from in place — there is nothing to fill in.
const MissingToken = () => (
  <Container className="mt-4">
    <Row>
      <Col>
        <Alert color="danger">
          This reset link is incomplete. Please use the link from your email, or
          request a new one.
        </Alert>
      </Col>
    </Row>
  </Container>
);

export default function () {
  const location = useLocation();
  const validation = new URLSearchParams(location.search).get("token");
  const [phase, setPhase] = useState("init");
  const { publicKey, ready } = useCaptcha();

  if (!validation) {
    return <MissingToken />;
  }

  if (!ready) {
    return <Loading.Content />;
  }

  return (
    <Section.Provider schema={AuthSchema}>
      <Section.Mutation
        guard={false}
        context={{ phase }}
        initial={{ password: "", confirm: "" }}
        mutation={RESET}
        schema={AuthSchema}
        target={{ $ref: "/Input/Reset/UI" }}
        onMutate={({ handler, state: { values } }) => {
          const { password, confirm } = values;

          if (password !== confirm) {
            return;
          }

          setPhase("init");
          CaptchaLib.request(publicKey, (token) => {
            handler({
              variables: { password, validation, token },
            });
          });
        }}
        onError={() => {
          setPhase("error");
        }}
        onCompleted={({
          viewer: {
            anonymous: {
              auth: { reset },
            },
          },
        }) => {
          if (reset) {
            setPhase("done");
          }
        }}
      >
        {MutationHandler}
      </Section.Mutation>
    </Section.Provider>
  );
};

const MutationHandler = ({
  schema,
  target,
  state: { values, setValues },
  context: { phase }
}) => {
  const formProps = {
    schema,
    target,
    values,
    onChange: (values) => {
      setValues(values);
    },
    config: {
      "/Input/Reset/UI": {
        fields: [
          {
            label: "New password",
            property: "password",
            password: true,
          },
          {
            label: "Confirm",
            property: "confirm",
            password: true,
          },
        ],
      },
    },
  };

  // Same reason as attach: the jsonschema validator behind @repoflow/schema has
  // no $data extension, so "equal to that field" cannot live in the schema.
  const { password, confirm } = values || {};
  const mismatch = Boolean(password) && Boolean(confirm) && password !== confirm;

  if (phase === "done") {
    return (
      <Container className="mt-4">
        <Row>
          <Col className="text-center">
            <h1>Password changed</h1>
            <p>You can now sign in with your new password.</p>
            {/* No domainId to hand back: the reset token carried it, and it is
                not in this page's query string. /auth sorts out where to go. */}
            <Link to={`/auth`}>Continue</Link>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col className="text-center">
          <h1>Choose a new password</h1>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="mt-4"></div>
          <Form {...formProps}>
            <Section.Button type={"submit"} disabled={mismatch}>
              Set password
            </Section.Button>
          </Form>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          {mismatch && (
            <Alert color="warning">The two passwords do not match.</Alert>
          )}
          {phase === "error" && (
            <Alert color="danger">
              We could not change your password. This link may have expired or
              already been used — reset links last one hour and work once.
              Please request a new one.
            </Alert>
          )}
        </Col>
      </Row>
    </Container>
  );
};
